// ============================================================================
// Hook de busca semântica/híbrida na KB (F2 Bloco 3/3)
// Chama Edge Function kb-search — modo padrão "hybrid" (FTS + semântica via RRF).
// F3 Bloco 3/3: adiciona useKbAsk (RAG Q&A com citações).
// ============================================================================

import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";


export type KbSearchMode = "hybrid" | "semantic";

export interface KbHybridResult {
  page_id: string;
  space_id: string;
  title: string;
  slug: string;
  snippet: string;
  fused_score: number;
  fts_rank: number;
  semantic_similarity: number;
  data_classification: string;
  updated_at: string;
}

export interface KbSemanticResult {
  page_id: string;
  space_id: string;
  title: string;
  slug: string;
  chunk_id: string;
  snippet: string;
  similarity: number;
  data_classification: string;
  updated_at: string;
}

export interface KbSearchResponse {
  ok: boolean;
  mode: KbSearchMode;
  query: string;
  results: Array<KbHybridResult | KbSemanticResult>;
  config: { model: string; dimension: number };
}

/**
 * Busca semântica/híbrida com debounce implícito (staleTime).
 * Query mínima: 2 caracteres.
 */
export function useKbSemanticSearch(
  query: string,
  spaceId?: string | null,
  mode: KbSearchMode = "hybrid",
) {
  const trimmed = query.trim();
  const enabled = trimmed.length >= 2;

  return useQuery({
    queryKey: ["kb", "semantic-search", mode, trimmed, spaceId ?? "all"],
    queryFn: async (): Promise<KbSearchResponse> => {
      try {
        const { data, error } = await supabase.functions.invoke<KbSearchResponse>(
          "kb-search",
          {
            body: {
              query: trimmed,
              space_id: spaceId ?? null,
              mode,
              limit: 20,
            },
          },
        );
        if (error) throw error;
        if (!data) throw new Error("Resposta vazia");
        return data;
      } catch (err) {
        logger.error("[useKbSemanticSearch]", "falha na busca", err);
        throw err;
      }
    },
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}

// ============================================================================
// F3 Bloco 3/3 — RAG Q&A (kb-ask)
// ============================================================================

export interface KbCitation {
  index: number;
  page_id: string;
  space_id: string;
  title: string;
  slug: string;
  snippet: string;
  score: number;
  similarity: number;
}

export interface KbAskResponse {
  ok: boolean;
  query: string;
  answer: string;
  citations: KbCitation[];
  model: string;
  usage: {
    embed_tokens: number;
    input_tokens: number;
    output_tokens: number;
    latency_ms?: number;
  };
}

/**
 * Pergunta em linguagem natural sobre a KB (RAG com citações).
 * Ideal para uso via useMutation (não cacheia para preservar créditos).
 */
export function useKbAsk() {
  return useMutation({
    mutationFn: async (params: {
      query: string;
      spaceId?: string | null;
      topK?: number;
    }): Promise<KbAskResponse> => {
      const { data, error } = await supabase.functions.invoke<KbAskResponse>("kb-ask", {
        body: {
          query: params.query,
          space_id: params.spaceId ?? null,
          top_k: params.topK ?? 6,
        },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error("Resposta inválida");
      return data;
    },
    onError: (err) => {
      logger.error("[useKbAsk]", "falha na pergunta", err);
    },
  });
}
